import React, { useCallback, useEffect, useRef } from 'react';

import { FormattedMessage } from 'react-intl-next';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import Tooltip from '@atlaskit/tooltip';

import { useFeatureExposedAnalytics } from '../analytics/feature-exposed-analytics';
import { useAnalytics } from '../analytics/use-analytics';
import { useEditionAwareness } from '../services/edition-awareness-context';
import { getLoggedInPage } from '../services/get-logged-in-page';
import { isEligibleForConfluenceBuyReverseTrialExperiment } from '../services/is-eligible-for-confluence-buy-reverse-trials';
import { isEligibleForEduDiscount } from '../services/is-eligible-for-edu-discount';
import { isEligibleForStandardTrialsUserAndStorageLimitBreakers } from '../services/is-eligible-for-standard-trials-user-and-storage-limit-breakers';
import { isProductOneOf } from '../services/is-product-one-of';
import { WAC_URL } from '../services/wac-urls';
import type { ExperimentCohort, ExperimentCohortControlVariation } from '../types';

import { AutomationFreeLimitModal } from './dialogs/automation-free-limit-modal';
import { EducationalDiscountModal } from './dialogs/educational-discount-modal';
import { FreeToStandardModal } from './dialogs/free-to-standard-modal';
import { FreeToStandardRefreshModal } from './dialogs/free-to-standard-refresh-modal';
import { PaymentDetailsPopup } from './dialogs/payment-details-popup';
import { PlanSelectionPageModal } from './dialogs/plan-selection-page-modal';
import { PremiumAddPaymentModal } from './dialogs/premium-add-payment-modal';
import { PremiumPopup } from './dialogs/premium-popup';
import { ReverseTrialPopupDialog } from './dialogs/reverse-trial-popup';
import { StandardAddPaymentModal } from './dialogs/standard-add-payment-modal';
import { StandardTrialLimitBreakersModal } from './dialogs/standard-trial-limit-breakers-modal';
import { EditionAwarenessLink } from './edition-awareness-button';

const AddPaymentLink = ({ href }: { href: string }) => {
	return (
		<Tooltip
			content={
				<FormattedMessage
					id="@atlassian/edition-awareness.link.add-payment-tooltip"
					defaultMessage="Add payment details to keep your subscription"
					description="Tooltip for add payment details button"
				/>
			}
			position="bottom"
		>
			<EditionAwarenessLink
				href={href}
				target="_blank"
				touchpointIntent="addPaymentDetails"
				linkId="predunning"
			/>
		</Tooltip>
	);
};

const JiraStandardTrialOverviewLink = () => {
	const { cloudId, environment, billingSourceSystem, userEmail, automationFreeLimitStatus } =
		useEditionAwareness();

	const fireExperimentExposureEvent = useFeatureExposedAnalytics();

	const fireAutomationFreeLimitExperimentExposure = useCallback(() => {
		if (automationFreeLimitStatus) {
			const automationLimitsCohort = FeatureGates.getExperimentValue(
				'growth_automation_limits_experiment',
				'cohort',
				'not-enrolled',
			);
			fireExperimentExposureEvent(
				'growth_automation_limits_experiment',
				automationLimitsCohort,
				'tako',
			);
		}
	}, [automationFreeLimitStatus, fireExperimentExposureEvent]);

	// This information should always be available, given the prior checks to get here
	if (!environment || !billingSourceSystem || !userEmail) {
		return <NoAction />;
	}

	const rawJstoUrl =
		`${WAC_URL[environment]}/software/jira?ref=edition_awareness_standard_trial_` +
		`${billingSourceSystem.toLowerCase()}&cloudSiteId=${cloudId}`;
	const jstoUrl = getLoggedInPage(userEmail, rawJstoUrl);

	return (
		<Tooltip
			content={
				<FormattedMessage
					id="@atlassian/edition-awareness.link.jira-standard-trial-overview-tooltip"
					defaultMessage="More about your trial and how to pay"
					description="Tooltip for trial information button"
				/>
			}
			position="bottom"
		>
			<EditionAwarenessLink
				href={jstoUrl}
				target="_blank"
				touchpointIntent="addPaymentDetails"
				linkId="jsto"
				onClick={fireAutomationFreeLimitExperimentExposure}
			/>
		</Tooltip>
	);
};

/**
 * Sends an event when there is no action available for the customer to render. This is an error state
 * which means that audience and timing has passed a customer as eligible when they shouldn't have.
 *
 * Next iteration on this would be to return a safe default action for all customers
 */
function NoAction() {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'actionsAndContent',
			actionSubjectId: 'editionAwareness',
			action: 'missed',
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent]);

	return null;
}

function FreeEdition() {
	const context = useEditionAwareness();
	const { product, siteType, isInCollection, userRoles, automationFreeLimitStatus } = context;
	const fireExperimentExposureEvent = useFeatureExposedAnalytics();

	if (
		isProductOneOf(product, ['jira', 'confluence']) &&
		isInCollection &&
		userRoles?.includes('BILLING_ADMIN') &&
		fg('teamwork_collection_ea_modal')
	) {
		return <PlanSelectionPageModal type="twc" />;
	}

	if (isEligibleForEduDiscount(context)) {
		const cohort = FeatureGates.getExperimentValue<ExperimentCohortControlVariation>(
			'offer_edu_org_discount',
			'cohort',
			'not-enrolled',
		);

		fireExperimentExposureEvent('offer_edu_org_discount', cohort, 'tako');

		if (cohort === 'variation') {
			return <EducationalDiscountModal />;
		}
	}

	if (fg('growth_automation_limits_kill_switch')) {
		if (automationFreeLimitStatus) {
			// These two statuses change the button content, and we need to fire exposure
			const fireExperimentExposure =
				automationFreeLimitStatus === 'BREACHED' ||
				automationFreeLimitStatus === 'PROJECTED_TO_BREACH';

			const cohort = FeatureGates.getExperimentValue<string>(
				'growth_automation_limits_experiment',
				'cohort',
				'not-enrolled',
				{ fireExperimentExposure },
			);

			if (fireExperimentExposure) {
				fireExperimentExposureEvent('growth_automation_limits_experiment', cohort, 'tako');
			}

			if (cohort === 'variation') {
				return <AutomationFreeLimitModal />;
			}
		}
	}

	if (
		product === 'jira' &&
		// We're excluding the edu experiment cohort from seeing the refresh experiment
		siteType !== 'EDU' &&
		siteType !== 'ORG' &&
		FeatureGates.getExperimentValue<string>(
			'jira_free_ea_modal_refresh',
			'cohort',
			'not-enrolled',
			{
				// We only want to fire the exposure when they view the modal.
				// The control experience is still inside of the product.
				fireExperimentExposure: false,
			},
		) === 'variation'
	) {
		return <FreeToStandardRefreshModal />;
	}

	return <FreeToStandardModal />;
}

function StandardEdition() {
	const context = useEditionAwareness();
	const {
		product,
		isInTrial,
		isInPredunning,
		isInvoiceable,
		canAddPaymentDetails,
		isInCollection,
		userRoles,
		addPaymentDetailsExperienceUrl,
		automationFreeLimitStatus,
	} = context;

	const fireExperimentExposureEvent = useFeatureExposedAnalytics();

	if (fg('growth_automation_limits_kill_switch')) {
		if (automationFreeLimitStatus) {
			const cohort = FeatureGates.getExperimentValue<string>(
				'growth_automation_limits_experiment',
				'cohort',
				'not-enrolled',
				{ fireExperimentExposure: false },
			);

			if (cohort === 'variation') {
				return <AutomationFreeLimitModal />;
			}
		}
	}

	if (
		isProductOneOf(product, ['jira', 'confluence']) &&
		isInCollection &&
		userRoles?.includes('BILLING_ADMIN') &&
		fg('teamwork_collection_ea_modal')
	) {
		return <PlanSelectionPageModal type="twc" />;
	}

	if ((isInTrial || isInPredunning) && isInvoiceable === false && canAddPaymentDetails) {
		if (
			// Only eligible for Confluence and Jira
			isEligibleForStandardTrialsUserAndStorageLimitBreakers(context)
		) {
			const abExperimentKey =
				product === 'jira'
					? 'jira_edition_awareness_standard_trials'
					: 'connie_edition_awareness_standard_trials';

			const abCohort = FeatureGates.getExperimentValue<ExperimentCohort>(
				abExperimentKey,
				'cohort',
				'not-enrolled',
			);

			fireExperimentExposureEvent(abExperimentKey, abCohort, 'luna');

			if (abCohort === 'treatment') {
				return <StandardTrialLimitBreakersModal />;
			}
		}

		if (
			isInTrial &&
			(product === 'jira-service-management' || product === 'jira-product-discovery')
		) {
			return <StandardAddPaymentModal />;
		}

		// Jira Standard - Site admins get routed to JSTO, others to payment popup
		if (product === 'jira' && isInTrial && userRoles?.includes('SITE_ADMIN')) {
			return <JiraStandardTrialOverviewLink />;
		}
		if (product === 'jira' && isInTrial && !userRoles?.includes('SITE_ADMIN')) {
			return <PaymentDetailsPopup />;
		}

		if (product === 'confluence') {
			return <StandardAddPaymentModal />;
		}

		if (
			addPaymentDetailsExperienceUrl &&
			isInPredunning &&
			isProductOneOf(product, ['jira', 'jira-service-management', 'jira-product-discovery'])
		) {
			return <AddPaymentLink href={addPaymentDetailsExperienceUrl} />;
		}
	}

	return <NoAction />;
}

function PremiumEdition() {
	const context = useEditionAwareness();
	const fireExperimentExposureEvent = useFeatureExposedAnalytics();

	const {
		product,
		isInTrial,
		isInPredunning,
		isInReverseTrial,
		isInCollection,
		addPaymentDetailsExperienceUrl,
		userRoles,
	} = context;

	// make sure twc is always shown first before jira reverse trial
	// otherwise, jira reverse trial will be shown with collection
	if (
		isProductOneOf(product, ['jira', 'confluence']) &&
		isInCollection &&
		userRoles?.includes('BILLING_ADMIN') &&
		fg('teamwork_collection_ea_modal')
	) {
		return <PlanSelectionPageModal type="twc" />;
	}

	if (
		product === 'jira' &&
		(isInTrial || isInPredunning) &&
		isInReverseTrial &&
		userRoles?.includes('BILLING_ADMIN') &&
		fg('jira_reverse_trial_ea_updates_during_trial_gate')
	) {
		const rtCohort = FeatureGates.getExperimentValue<ExperimentCohortControlVariation>(
			'jira_reverse_trial_ea_updates_during_trial',
			'cohort',
			'not-enrolled',
		);

		fireExperimentExposureEvent(
			'jira_reverse_trial_ea_updates_during_trial',
			rtCohort,
			'transmuters',
		);

		if (rtCohort === 'variation') {
			return <PlanSelectionPageModal type="reverse-trial" />;
		}
	}

	if (product === 'jira' && isInTrial && isInReverseTrial && addPaymentDetailsExperienceUrl) {
		return <ReverseTrialPopupDialog />;
	}

	if (product === 'jira' && isInTrial && !isInReverseTrial && addPaymentDetailsExperienceUrl) {
		return <PaymentDetailsPopup />;
	}

	if (
		isInPredunning &&
		isProductOneOf(product, ['jira', 'jira-service-management']) &&
		addPaymentDetailsExperienceUrl
	) {
		return <AddPaymentLink href={addPaymentDetailsExperienceUrl} />;
	}

	if (
		isEligibleForConfluenceBuyReverseTrialExperiment(context) &&
		fg('growth_confluence_buy_reverse_trials_fg')
	) {
		const abCohort = FeatureGates.getExperimentValue<ExperimentCohortControlVariation>(
			'growth_confluence_buy_reverse_trials_ab',
			'cohort',
			'not-enrolled',
		);

		fireExperimentExposureEvent('growth_confluence_buy_reverse_trials_ab', abCohort, 'luna');

		if (abCohort === 'variation') {
			return <PlanSelectionPageModal type="reverse-trial" />;
		}
	}

	if (product === 'confluence' && (isInTrial || isInPredunning)) {
		return <PremiumPopup />;
	}

	if (isInTrial && product === 'jira-service-management') {
		return <PremiumAddPaymentModal />;
	}

	return <NoAction />;
}

/**
 * Covers what button and action we show to the customer.
 *
 * Experiments here should just modify what is shown to the customer not if it is shown to the customer.
 */
export function ActionsAndContent() {
	const { edition } = useEditionAwareness();

	if (edition === 'free') {
		return <FreeEdition />;
	}

	if (edition === 'standard') {
		return <StandardEdition />;
	}

	if (edition === 'premium') {
		return <PremiumEdition />;
	}

	return <NoAction />;
}
